// src/components/EmployeeProfile.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import EmployeePersonalSection from "./EmployeePersonalSection";
import EmployeeAssignmentsSection from "./EmployeeAssignmentsSection";
import EmployeeWagesSection from "./EmployeeWagesSection";
import EmployeeSnapshotSection from "./EmployeeSnapshotSection";
import EmployeeEmploymentSection from "./EmployeeEmploymentSection";
import EmployeePermissionsSection from "./EmployeePermissionsSection";
import { canAccessFeature } from "../utils/permissions";
// import EmployeeDocumentsSection from "./EmployeeDocumentsSection";
// etc.

const ALL_SECTIONS = [
  { label: "Snapshot", key: "snapshot", requiredFeature: "employee_central" },
  { label: "Personal", key: "personal", requiredFeature: "employee_profile_personal" },
  { label: "Assignments", key: "assignments", requiredFeature: "employee_assignments_manage" },
  { label: "Wages", key: "wages", requiredFeature: "wages_section" },
  { label: "Documents", key: "documents", requiredFeature: "employee_profile_personal" },
  { label: "Permissions", key: "permissions", requiredFeature: "user_permissions" },
  { label: "Notes", key: "notes", requiredFeature: "employee_profile_personal" },
];

export default function EmployeeProfile() {
  const { employeeId } = useParams();
  const [sidebarTab, setSidebarTab] = useState(0); // Default: Snapshot
  const [userPermission, setUserPermission] = useState(null);
  const [availableSections, setAvailableSections] = useState([]);

  // Load user permissions from localStorage
  useEffect(() => {
    const adminUser = localStorage.getItem('adminUser');
    if (adminUser) {
      try {
        const user = JSON.parse(adminUser);
        setUserPermission(user.permissionLevel);
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  // Filter sections based on permissions
  useEffect(() => {
    if (userPermission) {
      const filtered = ALL_SECTIONS.filter(section => {
        return canAccessFeature(userPermission, section.requiredFeature);
      });
      setAvailableSections(filtered);
      
      if (sidebarTab >= filtered.length) {
        setSidebarTab(0);
      }
    } else {
      setAvailableSections([]);
    }
  }, [userPermission, sidebarTab]);

  return (
    <Box sx={{ display: "flex", minHeight: "80vh" }}>
      {/* Sidebar navigation */}
      <Paper elevation={1} sx={{ minWidth: 184, pt: 1.5, borderRadius: 0 }}>
        <Typography
          variant="h6"
          align="center"
          sx={{ mb: 1.5, fontWeight: 700, fontSize: "1rem", letterSpacing: 0.8 }}
        >
          Employee
        </Typography>
        <Divider />
        <List>
          {availableSections.map((section, idx) => (
            <ListItem key={section.key} disablePadding>
              <ListItemButton
                selected={sidebarTab === idx}
                onClick={() => setSidebarTab(idx)}
                sx={{ minHeight: 54 }}
              >
                <ListItemText
                  primary={section.label}
                  primaryTypographyProps={{
                    fontSize: '0.98rem',
                    fontWeight: sidebarTab === idx ? "bold" : "normal",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Main content area */}
      <Box sx={{ flex: 1, px: 4, py: 3, pl: 4.5 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {availableSections[sidebarTab]?.label || 'Loading...'}
        </Typography>

        {/* SNAPSHOT section */}
        {availableSections[sidebarTab]?.key === "snapshot" ? (
          <EmployeeSnapshotSection employeeId={employeeId} />
        ) : 
        /* PERSONAL section rendered as a single long page */
        availableSections[sidebarTab]?.key === "personal" ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, mt: 0.5, maxWidth: 1120 }}>
            <Paper
              elevation={0}
              sx={{
                px: 2.5,
                py: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(29,78,216,0.06) 0%, rgba(13,148,136,0.05) 100%)',
              }}
            >
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'primary.main' }}>
                Employee Profile
              </Typography>
              <Typography sx={{ mt: 0.55, fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 900, letterSpacing: -0.8, lineHeight: 1 }}>
                Personal
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65, maxWidth: 760, lineHeight: 1.6 }}>
                Review and edit personal details, employment information, and emergency contact information in one clear profile workspace.
              </Typography>
            </Paper>
            <Box>
              <EmployeePersonalSection employeeId={employeeId} show="personal" />
            </Box>
            <Box>
              <EmployeeEmploymentSection employeeId={employeeId} />
            </Box>
            <Box>
              <EmployeePersonalSection employeeId={employeeId} show="emergency" />
            </Box>
          </Box>
        ) : availableSections[sidebarTab]?.key === "assignments" ? (
          <EmployeeAssignmentsSection employeeId={employeeId} />
        ) : availableSections[sidebarTab]?.key === "wages" ? (
          <EmployeeWagesSection employeeId={employeeId} />
        ) : availableSections[sidebarTab]?.key === "permissions" ? (
          <EmployeePermissionsSection employeeId={employeeId} />
        ) : (
          <Box>
            <Typography variant="body1" sx={{ color: "#aaa", mt: 2 }}>
              Section under construction.
            </Typography>
            {/* Example for future sections:
            {SECTIONS[sidebarTab].key === "documents" && (
              <EmployeeDocumentsSection employeeId={employeeId} />
            )} */}
          </Box>
        )}
      </Box>
    </Box>
  );
}
